// ---------------------------------------------------------------------------
// Social URL Builder
// ---------------------------------------------------------------------------
// Utility functions to construct URLs for social media profiles and posts.
// Used by cell renderers to create external links in Discovery tables.
// ---------------------------------------------------------------------------

export type SocialPlatform = 'instagram' | 'x' | 'twitter' | 'tiktok' | 'youtube'

/**
 * Builds the profile URL for a given platform and username.
 * @param platform - The social media platform identifier
 * @param username - The username (with or without @ prefix)
 * @returns The full profile URL, or null if platform is not supported
 */
export function buildProfileUrl(platform: string, username: string): string | null {
  if (!username) return null
  const clean = username.replace('@', '')
  const normalizedPlatform = platform.toLowerCase()

  switch (normalizedPlatform) {
    case 'instagram':
      return `https://instagram.com/${clean}`
    case 'x':
    case 'twitter':
      return `https://x.com/${clean}`
    case 'tiktok':
      return `https://tiktok.com/@${clean}`
    case 'youtube':
      return `https://youtube.com/@${clean}`
    default:
      return null
  }
}

/**
 * Builds the post URL for a given platform, post ID, and optional author.
 * @param platform - The social media platform identifier
 * @param id - The post/content ID (shortcode for Instagram, status ID for X, etc.)
 * @param author - The author username (required for X and TikTok)
 * @returns The full post URL, or null if platform is not supported or required data is missing
 */
export function buildPostUrl(platform: string, id: string, author?: string): string | null {
  if (!id) return null
  const normalizedPlatform = platform.toLowerCase()
  const cleanAuthor = author?.replace('@', '')

  switch (normalizedPlatform) {
    case 'instagram':
      return `https://instagram.com/p/${id}`
    case 'x':
    case 'twitter':
      return cleanAuthor ? `https://x.com/${cleanAuthor}/status/${id}` : null
    case 'tiktok':
      return cleanAuthor ? `https://tiktok.com/@${cleanAuthor}/video/${id}` : null
    case 'youtube':
      return `https://youtube.com/watch?v=${id}`
    default:
      return null
  }
}
